/**
 * The elastic clock's tier table (backend.md §3.5). The room consumes it in M3;
 * it lives here in M2 because `RoomConfig`, the profile's `defaultPaceTier` and
 * `GET /api/config` all name a tier and must agree on the set.
 */
import { z } from 'zod';
/** Slowest first. */
export const PACE_TIERS = ['sinReloj', 'tranquilo', 'normal', 'rapido', 'muyRapido'];
export const paceTierSchema = z.enum(PACE_TIERS);
export const DEFAULT_PACE_TIERS = {
    sinReloj: {
        baseMs: null,
        reserveMs: 0,
        refillPerHandMs: 0,
        reconnectGraceMs: 120_000,
        ranked: false,
    },
    tranquilo: {
        baseMs: 12_000,
        reserveMs: 90_000,
        refillPerHandMs: 4_000,
        reconnectGraceMs: 120_000,
        ranked: true,
    },
    normal: {
        baseMs: 8_000,
        reserveMs: 60_000,
        refillPerHandMs: 3_000,
        reconnectGraceMs: 90_000,
        ranked: true,
    },
    rapido: {
        baseMs: 5_000,
        reserveMs: 40_000,
        refillPerHandMs: 2_000,
        reconnectGraceMs: 60_000,
        ranked: true,
    },
    muyRapido: {
        baseMs: 3_000,
        reserveMs: 25_000,
        refillPerHandMs: 1_500,
        reconnectGraceMs: 45_000,
        ranked: true,
    },
};
/**
 * Even an untimed table cannot wedge forever: `sinReloj` still arms this cap
 * (backend.md §3.5).
 */
export const AFK_HARD_MS = 300_000;
/**
 * Consecutive timeouts by one seat before the room plays for it automatically
 * (backend.md §3.5). The streak resets the moment that player acts again.
 */
export const AUTOPILOT_TIMEOUT_STREAK = 3;
/**
 * How long the autopilot waits before playing a turn for a seat whose player is
 * *connected* (playtest 2, item 7).
 *
 * The substitute exists so an absent player cannot stall three others. A player
 * sitting right there is not that case, and the bot's normal 400–900 ms think
 * time made the seat unrecoverable: every turn was gone before a human could
 * read it, so "the autopilot stands down when you act" was a promise the player
 * could not physically keep. A seat still held by someone at the keyboard gets
 * a beat long enough to act in; a disconnected one keeps the fast tick, because
 * there the whole point is that nobody is coming.
 *
 * `room.resume` is the deliberate way out and does not wait for this at all.
 */
export const AUTOPILOT_PRESENT_GRACE_MS = 4_500;
/**
 * The pause between two seats' pares/juego declarations (playtest 2, item 9).
 *
 * Declarations carry no decision — the engine knows the hands and writes the
 * truth itself — so the room dispatches all four rather than asking four people
 * to press a button that has only one answer. What the pause buys is the thing
 * the button was accidentally providing: time to *see* who has pares and who
 * has juego. Four seats at this interval is a shade under three seconds, which
 * is the number the owner asked for, and then betting opens.
 */
export const DECLARE_STEP_MS = 700;
/**
 * How long the table sits on a revealed hand before the next one is dealt
 * (backend.md §3.3). The longer pause follows an órdago, whose reveal is the
 * whole game and deserves the beat.
 *
 * Raised from 2.5 s / 4 s in v1.1 (playtest item 6). The old numbers were sized
 * for the reveal *animation* to land; what they were actually gating is four
 * people reading who won each of up to five lances and where the piedras went,
 * in a language half of them are still learning. The owner's note was that
 * there is no time to read it, and there was not: 2.5 s is under a second per
 * line. Nothing is gated on the wait — the summary can be dismissed at any
 * point with a click, a key or the Continue button — so the cost of the longer
 * pause falls only on players who have already finished reading, and they have
 * a button.
 */
export const REVEAL_DELAY_MS = 6_500;
export const REVEAL_DELAY_ORDAGO_MS = 8_000;
/** A finished table stays open this long so everyone can read the stats. */
export const MATCH_END_DISPOSE_MS = 60_000;
/**
 * Absence budget before a seat forfeits: two expired grace windows, or five
 * cumulative minutes away, whichever comes first (backend.md §3.6).
 */
export const FORFEIT_GRACE_EXPIRIES = 2;
export const FORFEIT_ABSENT_MS = 300_000;
const paceTierSpecPatchSchema = z
    .object({
    baseMs: z.number().int().min(1000).max(600_000).nullable(),
    reserveMs: z.number().int().min(0).max(3_600_000),
    refillPerHandMs: z.number().int().min(0).max(600_000),
    reconnectGraceMs: z.number().int().min(0).max(600_000),
    ranked: z.boolean(),
})
    .partial();
/** Shape of `PACE_TIERS_JSON`: a sparse patch over `DEFAULT_PACE_TIERS`. */
export const paceTiersOverrideSchema = z.record(paceTierSchema, paceTierSpecPatchSchema);
/**
 * Applies a sparse env override onto the built-in table. Keys present but
 * `undefined` are dropped rather than spread, because under
 * `exactOptionalPropertyTypes` an explicit `undefined` is a value, not an
 * absence, and would widen every field of the result.
 */
export function resolvePaceTiers(override) {
    const out = {};
    for (const tier of PACE_TIERS) {
        const patch = override?.[tier];
        const spec = { ...DEFAULT_PACE_TIERS[tier] };
        if (patch !== undefined) {
            if (patch.baseMs !== undefined)
                spec.baseMs = patch.baseMs;
            if (patch.reserveMs !== undefined)
                spec.reserveMs = patch.reserveMs;
            if (patch.refillPerHandMs !== undefined)
                spec.refillPerHandMs = patch.refillPerHandMs;
            if (patch.reconnectGraceMs !== undefined)
                spec.reconnectGraceMs = patch.reconnectGraceMs;
            if (patch.ranked !== undefined)
                spec.ranked = patch.ranked;
        }
        out[tier] = spec;
    }
    return out;
}
//# sourceMappingURL=pace.js.map